import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Mail, Key, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminOTPLogin() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [step, setStep] = useState("email"); // "email" | "otp"
  const [email, setEmail] = useState("gio@giopilos.com");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [verifyError, setVerifyError] = useState("");
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const verifyInFlight = useRef(false);

  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const updateCooldown = () => {
      const remaining = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownSeconds(remaining);
      if (remaining === 0) setCooldownUntil(null);
    };
    updateCooldown();
    const timer = setInterval(updateCooldown, 1000);
    return () => clearInterval(timer);
  }, [cooldownUntil]);

  // Check for existing valid session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const token = localStorage.getItem("gio_token");
      
      if (token) {
        try {
          // Verify token is still valid
          await api.get("/api/admin-otp/verify-session");
          await loginWithToken(token);
          
          // Token valid - redirect to dashboard
          toast.success("Welcome back! Already logged in 🎉");
          navigate("/crm");
          return;
        } catch (err) {
          // Token expired/invalid - clear and show login
          localStorage.removeItem("gio_token");
          localStorage.removeItem("otp_verified");
        }
      }
      
      setCheckingSession(false);
    };

    checkExistingSession();
  }, [navigate, loginWithToken]);

  const requestOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email required");
      return;
    }

    setLoading(true);
    try {
      await api.post("/api/admin-otp/request", { email: email.trim().toLowerCase() });
      toast.success("OTP sent to your email!");
      setVerificationAttempts(0);
      setVerifyError("");
      setStep("otp");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    if (verifyInFlight.current || cooldownSeconds > 0) return;
    if (!otp.trim()) {
      toast.error("Enter the 6-digit code");
      return;
    }

    verifyInFlight.current = true;
    setLoading(true);
    try {
      const res = await api.post("/api/admin-otp/verify", { 
        email: email.trim().toLowerCase(),
        code: String(otp).trim()
      });
      const token = res.data.token;
      
      // Store token with correct key
      await loginWithToken(token);
      localStorage.setItem("otp_verified", "true"); // Flag for /login access
      toast.success("Welcome back! 🎉");
      
      // Redirect to CRM master dashboard
      navigate("/crm");
    } catch (err) {
      const responseError = err?.response?.data;
      const errorCode = responseError?.code;
      const message = responseError?.message || responseError?.detail || "Unable to verify this code.";
      setVerifyError(errorCode ? `${message} (${errorCode})` : message);
      toast.error(message);
      if (errorCode === "wrong_code") {
        setVerificationAttempts((attempts) => attempts + 1);
      } else if (errorCode === "attempt_limit" || errorCode === "cooldown_active") {
        setVerificationAttempts(5);
        setCooldownUntil(Date.now() + 15 * 60 * 1000);
      }
      setOtp("");
    } finally {
      verifyInFlight.current = false;
      setLoading(false);
    }
  };

  // Show loading while checking existing session
  if (checkingSession) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)" }}>
        <div className="text-center">
          <RefreshCw size={48} className="animate-spin mx-auto mb-4" style={{ color: "#a855f7" }} />
          <p className="text-zinc-400">Checking session...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)" }}>
      
      <div 
        className="w-full max-w-md rounded-2xl p-8 border"
        style={{
          background: "rgba(24, 24, 27, 0.8)",
          backdropFilter: "blur(20px)",
          borderColor: "rgba(168, 85, 247, 0.2)",
          boxShadow: "0 20px 60px -10px rgba(168, 85, 247, 0.3)",
        }}>
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ background: "rgba(168, 85, 247, 0.1)" }}>
            <Shield size={32} style={{ color: "#a855f7" }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: "#fff" }}>
            Admin Control
          </h1>
          <p className="text-sm" style={{ color: "#71717a" }}>
            Secure OTP Authentication
          </p>
        </div>

        {/* Email Step */}
        {step === "email" && (
          <form onSubmit={requestOTP} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#a1a1aa" }}>
                Admin Email
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#71717a" }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm"
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    borderColor: "rgba(113, 113, 122, 0.3)",
                    color: "#fff",
                  }}
                  disabled={loading}
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
                color: "#fff",
              }}>
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Code
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        )}

        {/* OTP Step */}
        {step === "otp" && (
          <form onSubmit={verifyOTP} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#a1a1aa" }}>
                Enter 6-Digit Code
              </label>
              <p className="text-xs mb-3" style={{ color: "#71717a" }}>
                We sent a code to <strong style={{ color: "#a855f7" }}>{email}</strong>
              </p>
              <div className="relative">
                <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#71717a" }} />
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setVerifyError("");
                  }}
                  placeholder="000000"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border text-center text-2xl font-mono tracking-widest"
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    borderColor: "rgba(113, 113, 122, 0.3)",
                    color: "#a855f7",
                  }}
                  disabled={loading}
                  autoFocus
                  maxLength={6}
                />
              </div>
            </div>

            {verifyError && <p role="alert" className="text-sm text-red-400">{verifyError}</p>}
            {cooldownSeconds > 0 && (
              <p role="status" className="text-sm text-amber-300">
                Too many attempts. Try again in {Math.floor(cooldownSeconds / 60)}:{String(cooldownSeconds % 60).padStart(2, "0")}.
              </p>
            )}

            <button
              type="submit"
              disabled={loading || otp.length !== 6 || verificationAttempts >= 5 || cooldownSeconds > 0}
              className="w-full py-3 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
                color: "#fff",
              }}>
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify & Login
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep("email");
                setOtp("");
              }}
              className="w-full py-2 text-sm"
              style={{ color: "#71717a" }}>
              ← Back to email
            </button>
          </form>
        )}

        {/* Footer */}
        <div className="mt-6 pt-6 border-t text-center" style={{ borderColor: "rgba(113, 113, 122, 0.2)" }}>
          <p className="text-xs" style={{ color: "#71717a" }}>
            🔒 Protected by one-time password authentication
          </p>
        </div>
      </div>
    </div>
  );
}
